// src/components/admin/AdminClientEditPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Container, Typography, Paper, Alert, Box,
  IconButton, Tooltip, Breadcrumbs, Link, Chip,
  Fade, Skeleton, Button, Grid
} from '@mui/material';
import {
  ArrowBack, Edit, Business
} from '@mui/icons-material';
import { adminApi } from '../../api/adminApi';
import AdminClientForm from './AdminClientForm';
import './AdminClientEditPage.css';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.6
};

export default function AdminClientEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchClient = async () => {
    setLoading(true);
    setError('');
    
    try {
      const clientData = await adminApi.getClientDetail(id);
      setClient(clientData);
    } catch (err) {
      console.error('Error fetching client:', err);
      setError(err.message || 'فشل في تحميل بيانات العميل');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadClient = async () => {
      setLoading(true);
      setError('');
      
      try {
        const clientData = await adminApi.getClientDetail(id);
        setClient(clientData);
      } catch (err) {
        console.error('Error fetching client:', err);
        setError(err.message || 'فشل في تحميل بيانات العميل');
      } finally {
        setLoading(false);
      }
    };
    
    loadClient();
  }, [id]);

  const handleEditClient = async (data) => {
    setSaving(true);
    try {
      // لا ترسل كلمة المرور إذا كانت فارغة
      if (!data.password) {
        delete data.password;
      }
      
      await adminApi.updateClient(id, data);
      
      // عرض رسالة نجاح
      alert('تم تحديث بيانات العميل بنجاح!');
      navigate('/admin/clients');
    } catch (error) {
      console.error('Failed to edit client:', error);
      alert(error.message || 'فشل في تحديث بيانات العميل');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('هل أنت متأكد من إلغاء التعديل؟ سيتم فقدان جميع التغييرات.')) {
      navigate('/admin/clients');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="text" width="60%" height={32} />
        </Box>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Grid item xs={12} md={6} key={item}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchClient}>
              إعادة المحاولة
            </Button>
          }
        >
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/clients')}
        >
          العودة لقائمة العملاء
        </Button>
      </Container>
    );
  }

  if (!client) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h5" color="text.secondary" textAlign="center">
          العميل غير موجود
        </Typography>
      </Container>
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link
              color="inherit"
              href="/admin/clients"
              onClick={(e) => {
                e.preventDefault();
                navigate('/admin/clients');
              }}
              sx={{ 
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              العملاء
            </Link>
            <Link
              color="inherit"
              href={`/admin/clients/${id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/admin/clients/${id}`);
              }}
              sx={{ 
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {client.company_name}
            </Link>
            <Typography color="text.primary">تعديل</Typography>
          </Breadcrumbs>

          {/* Title and Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                bgcolor: 'primary.main', 
                borderRadius: 2, 
                display: 'flex', 
                alignItems: 'center' 
              }}>
                <Edit sx={{ color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h4" component="h1" fontWeight="700" gutterBottom>
                  تعديل بيانات العميل
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  تحديث معلومات {client.company_name}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="العودة">
                <IconButton
                  onClick={() => navigate('/admin/clients')}
                  sx={{
                    bgcolor: 'grey.100',
                    '&:hover': { bgcolor: 'grey.200' }
                  }}
                >
                  <ArrowBack />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Client Info Card */}
        <Paper sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Business color="primary" />
            <Typography variant="h6" fontWeight="600">
              معلومات العميل الحالية
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Chip
              label={`الشركة: ${client.company_name}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`البريد: ${client.email}`}
              color="info"
              variant="outlined"
            />
            <Chip
              label={client.is_active ? 'البوت يعمل' : 'البوت متوقف'}
              color={client.is_active ? 'success' : 'error'}
              variant="outlined"
            />
          </Box>
        </Paper>

        {/* Edit Form */}
        <Fade in={true} timeout={800}>
          <Paper sx={{
            p: 4,
            borderRadius: 3,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
          }}>
            <AdminClientForm 
              onSubmit={handleEditClient} 
              defaultValues={client} 
              isEdit={true}
              clientId={id}
              saving={saving}
              onCancel={handleCancel}
            />
          </Paper>
        </Fade>
      </Container>
    </motion.div>
  );
}
